import { baseApi, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import type { DeliveryOperations, DeliveryReconciliation, DeliveryStatus } from '../model/types'
import { decodeDeliveryOperations, decodeDeliveryReconciliation, decodeDeliveryStatus } from './contracts'
import { AQSI_DELIVERY_URLS } from './urls'

/**
 * Endpoints статуса доставки каталога в ОБЩЕМ `baseApi`: имена `aqsiDelivery*`,
 * rid — аргумент каждого запроса, ключ кэша и тегов (два AQSI не смешиваются).
 */
export const AQSI_DELIVERY_TAG = 'AqsiDelivery' as const

export interface DeliveryArgs {
  resourceId: string
}

const tags = (resourceId: string, what: string) => [resourceTag(AQSI_DELIVERY_TAG, resourceId, what), resourceScopeTag(resourceId)]

export const deliveryApi = baseApi.enhanceEndpoints({ addTagTypes: [AQSI_DELIVERY_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    aqsiDeliveryStatus: build.query<DeliveryStatus, DeliveryArgs>({
      query: ({ resourceId }) => ({
        url: AQSI_DELIVERY_URLS.status(resourceId),
        decode: (raw) => decodeDeliveryStatus(raw, resourceId),
      }),
      providesTags: (_result, _error, { resourceId }) => tags(resourceId, 'STATUS'),
    }),
    aqsiDeliveryOperations: build.query<DeliveryOperations, DeliveryArgs>({
      query: ({ resourceId }) => ({
        url: AQSI_DELIVERY_URLS.operations(resourceId),
        decode: (raw) => decodeDeliveryOperations(raw, resourceId),
      }),
      providesTags: (_result, _error, { resourceId }) => tags(resourceId, 'OPERATIONS'),
    }),
    /** До первой сверки сервер отвечает 404 `reconciliation-not-run` — экран показывает это словами, не ошибкой. */
    aqsiDeliveryReconciliation: build.query<DeliveryReconciliation, DeliveryArgs>({
      query: ({ resourceId }) => ({
        url: AQSI_DELIVERY_URLS.reconciliation(resourceId),
        decode: (raw) => decodeDeliveryReconciliation(raw, resourceId),
      }),
      providesTags: (_result, _error, { resourceId }) => tags(resourceId, 'RECONCILIATION'),
    }),
    /** Сверка привязок со снимком каталога кабинета: чистый пересчёт, повтор безопасен (право `delivery.reconcile`). */
    aqsiDeliveryReconcile: build.mutation<DeliveryReconciliation, DeliveryArgs>({
      query: ({ resourceId }) => ({
        url: AQSI_DELIVERY_URLS.reconciliation(resourceId),
        method: 'POST',
        decode: (raw) => decodeDeliveryReconciliation(raw, resourceId),
      }),
      invalidatesTags: (_result, _error, { resourceId }) => [
        resourceTag(AQSI_DELIVERY_TAG, resourceId, 'RECONCILIATION'),
        resourceTag(AQSI_DELIVERY_TAG, resourceId, 'STATUS'),
      ],
    }),
  }),
})

export const { aqsiDeliveryStatus, aqsiDeliveryOperations, aqsiDeliveryReconciliation, aqsiDeliveryReconcile } =
  deliveryApi.endpoints
